import { Button } from "primereact/button";
import { FloatLabel } from "primereact/floatlabel";
import { Password } from "primereact/password";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToastContext } from "../Lib/Contexts/toastContext";
import { useConsultantCreatePassword } from "../hooks/useGetDataQuery";

const PasswordCreation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToastContext();
  const [searchParams] = useSearchParams();
  const { mutate: createPassword, isPending: isCreatingPassword } = useConsultantCreatePassword();

  const [decodedToken, setDecodedToken] = useState<string | null>(null);

  const [password, setPassword] = useState<string>();
  const [confirmPassword, setConfirmPassword] = useState<string>();

  const [passwordError, setPasswordError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");

  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  useEffect(() => {
    const encodedToken = searchParams.get("token");

    if (encodedToken) {
      const decoded = decodeURIComponent(encodedToken);
      setDecodedToken(decoded);
    }
  }, [searchParams]);

  const handleConfirmPassword = () => {
    let valid = true;
    if (!password) {
      setPasswordError("requiredField");
      valid = false;
    }

    if (!confirmPassword) {
      setConfirmPasswordError("requiredField");
      valid = false;
    }

    if (!valid) return;

    if (password !== confirmPassword) {
      showToast({
        severity: "error",
        summary: t("error"),
        detail: t("passwordConfirmationError"),
      });
      return;
    }

    const payload = {
      password: password,
      token: decodedToken,
      confirmPassword: confirmPassword,
    };

    createPassword(payload, {
      onSuccess: () => {
        setIsSuccess(true);
      },
      onError: () => {
        showToast({ severity: 'error', summary: t('error'), detail: t('consultantPasswordCreationFailed') });
      }
    });
  };

  return (
    <>
      <img
        className="flex "
        src={
          isSuccess ? "/passwordResetSuccessIcon.png" : "/resetPasswordIcon.png"
        }
        alt="Consultant Password Creation Image"
      />
      {!isSuccess && (
        <p
          className="text-center text-4xl m-0 font-bold mt-4"
          style={{ color: "#2D3967" }}
        >
          {t("consultantPasswordCreation")}
        </p>
      )}
      {isSuccess && (
        <p
          className="text-center text-4xl m-0 font-bold mt-4"
          style={{ color: "#2D3967" }}
        >
          {t("consultantPasswordCreationSuccess")}
        </p>
      )}
      {!isSuccess && (
        <p
          className="text-center text-lg m-0 mt-2"
          style={{ color: "#727A99" }}
        >
          {t("consultantPasswordCreationDescription")}
        </p>
      )}

      {!isSuccess && (
        <div className="w-full">
          <FloatLabel className="w-full mt-4">
            <Password
              invalid={passwordError != "" && !password}
              feedback={false}
              id="password"
              className="p-inputtext-lg w-full password"
              toggleMask
              variant="filled"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleConfirmPassword();
                }
              }}
            />
            <label htmlFor="password">{t("password")}</label>
          </FloatLabel>
          {passwordError != "" && !password && (
            <small className="text-red-500 text-xs">{t(passwordError)}</small>
          )}
        </div>
      )}
      {!isSuccess && (
        <div className="w-full">
          <FloatLabel className="w-full mt-4">
            <Password
              invalid={confirmPasswordError != "" && !confirmPassword}
              feedback={false}
              id="passwordConfirmation"
              className="p-inputtext-lg w-full password"
              toggleMask
              variant="filled"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleConfirmPassword();
                }
              }}
            />
            <label htmlFor="passwordConfirmation">
              {t("confirmPassword")}
            </label>
          </FloatLabel>
          {confirmPasswordError != "" && !confirmPassword && (
            <small className="text-red-500 text-xs">
              {t(confirmPasswordError)}
            </small>
          )}
        </div>
      )}

      <Button
        style={{
          backgroundColor: "#6383F4",
          borderRadius: "10px",
          border: "none",
        }}
        className="w-full mt-4"
        size="large"
        loading={isCreatingPassword}
        label={isSuccess ? t("backToLogin") : t("createPassword")}
        onClick={
          isSuccess ? () => navigate({ pathname: "/" }) : handleConfirmPassword
        }
      />
    </>
  );
};

export default PasswordCreation;
